import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Loader2, ImageOff } from "lucide-react";
import api from "../lib/apiClient";
import { SectionHeading } from "./SectionHeading";

const TABS = [
  { value: "ALL", label: "Tous" },
  { value: "ELECTROTECHNIQUE", label: "Électrotechnique" },
  { value: "IRVE", label: "IRVE" },
  { value: "LOCATION", label: "Location" },
  { value: "DAO", label: "DAO" },
];

const CAT_COLOR = {
  ELECTROTECHNIQUE: "bg-[#0F76B8] text-white",
  IRVE: "bg-[#3DAE2B] text-white",
  LOCATION: "bg-[#FBBF24] text-[#0F172A]",
  DAO: "bg-[#1B3A6B] text-white",
};
const CAT_LABEL = {
  ELECTROTECHNIQUE: "Électrotechnique",
  IRVE: "IRVE",
  LOCATION: "Location",
  DAO: "DAO",
};

export function RealisationsSection() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [active, setActive] = useState("ALL");

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/realisations");
        setItems(data);
      } catch {
        setItems([]);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const filtered = active === "ALL" ? items : items.filter((i) => i.category === active);
  const visibleTabs = TABS.filter((t) => t.value === "ALL" || items.some((i) => i.category === t.value));

  // Hide section entirely if no realisations yet
  if (!loading && items.length === 0) return null;

  return (
    <section className="bg-white border-y border-slate-200" data-testid="realisations-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 lg:py-24">
        <SectionHeading
          eyebrow="Nos réalisations"
          title="Chantiers récents"
          subtitle="Un aperçu de nos interventions sur l'île de La Réunion"
          testId="realisations-heading"
        />

        {/* Tabs */}
        {!loading && visibleTabs.length > 1 && (
          <div className="mt-10 flex flex-wrap justify-center gap-2" data-testid="realisations-tabs">
            {visibleTabs.map((t) => {
              const n = t.value === "ALL" ? items.length : items.filter((i) => i.category === t.value).length;
              return (
                <button
                  key={t.value}
                  onClick={() => setActive(t.value)}
                  data-testid={`realisations-tab-${t.value}`}
                  className={`px-5 h-10 rounded-full text-sm font-semibold transition-all ${
                    active === t.value
                      ? "bg-[#1B3A6B] text-white shadow-lg shadow-blue-900/20"
                      : "bg-slate-100 text-slate-700 hover:bg-slate-200"
                  }`}
                >
                  {t.label} <span className="opacity-60 ml-1 text-xs">{n}</span>
                </button>
              );
            })}
          </div>
        )}

        <div className="mt-10">
          {loading ? (
            <div className="py-20 text-center text-slate-400">
              <Loader2 className="w-6 h-6 animate-spin inline" />
            </div>
          ) : filtered.length === 0 ? (
            <div className="py-16 text-center text-slate-500">
              <ImageOff className="w-10 h-10 mx-auto mb-3 text-slate-300" />
              Aucun chantier dans cette catégorie pour l'instant.
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5" data-testid="realisations-grid">
              {filtered.slice(0, 9).map((r) => (
                <article
                  key={r.id}
                  data-testid={`realisation-${r.id}`}
                  className="group relative overflow-hidden rounded-2xl border border-slate-200 bg-slate-50 hover:-translate-y-1 hover:shadow-[0_25px_60px_-20px_rgba(27,58,107,0.35)] transition-all duration-300"
                >
                  <div className="relative aspect-[4/3] overflow-hidden">
                    <img
                      src={r.image_url}
                      alt={r.title}
                      loading="lazy"
                      className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/10 to-transparent" />
                    <div className={`absolute top-3 left-3 px-2.5 py-0.5 rounded-full text-[11px] font-bold ${CAT_COLOR[r.category] || "bg-slate-900 text-white"}`}>
                      {CAT_LABEL[r.category] || r.category}
                    </div>
                    <div className="absolute bottom-0 left-0 right-0 p-5">
                      <h3 className="text-white font-display font-bold text-lg leading-tight line-clamp-2">
                        {r.title}
                      </h3>
                      {r.description && (
                        <p className="mt-1.5 text-slate-200 text-xs line-clamp-2 leading-relaxed">
                          {r.description}
                        </p>
                      )}
                    </div>
                  </div>
                </article>
              ))}
            </div>
          )}
        </div>

        <div className="mt-12 text-center">
          <Link
            to="/contact"
            data-testid="realisations-cta"
            className="inline-flex items-center gap-2 h-12 px-7 rounded-full bg-[#1B3A6B] hover:bg-[#142c54] text-white font-semibold text-sm transition-colors shadow-lg shadow-blue-900/20"
          >
            Discutons de votre projet
            <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
      </div>
    </section>
  );
}

export default RealisationsSection;
